import { Component, inject, effect, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { forkJoin, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../../environments/environment';

/** Tempo máximo aguardando o processamento do callback antes de assumir que travou. */
const WATCHDOG_TIMEOUT_MS = 15000;

/**
 * Página de callback após autenticação no Keycloak
 *
 * A biblioteca angular-auth-oidc-client processa o callback automaticamente.
 * Esta página aguarda o processamento e redireciona o usuário — mas nunca fica
 * presa indefinidamente: falha de autenticação, falha ao montar o perfil ou
 * travamento (watchdog) saem do spinner e oferecem uma ação de retry/reload.
 */
@Component({
  selector: 'app-auth-callback',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './auth-callback.page.html',
})
export class AuthCallbackPage {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly oidcSecurityService = inject(OidcSecurityService);

  protected readonly hasRedirected = signal(false);
  protected readonly hasLoggedTokenInfo = signal(false);
  private readonly timedOut = signal(false);

  /** Mensagem de erro exibida no lugar do spinner; `null` enquanto o callback está em andamento. */
  protected readonly errorMessage = computed<string | null>(() => {
    if (this.timedOut()) {
      return 'A autenticação está demorando mais que o esperado. Tente recarregar.';
    }

    const authError = this.authService.authError();
    if (authError) {
      return authError;
    }

    if (!this.authService.isLoading() && !this.authService.user()) {
      return 'Não foi possível concluir a autenticação. Faça login novamente.';
    }

    return null;
  });

  protected readonly retryLabel = computed(() =>
    this.timedOut() || !!this.authService.authError() ? 'Recarregar' : 'Tentar novamente'
  );

  constructor() {
    // Aguarda autenticação ser processada automaticamente pela biblioteca
    effect(() => {
      const user = this.authService.user();
      const isLoading = this.authService.isLoading();

      // Quando autenticação completar e usuário estiver disponível
      if (user && !isLoading && !this.hasRedirected()) {
        console.log('[AUTH-CALLBACK] Autenticação bem-sucedida, redirecionando...');

        if (!this.hasLoggedTokenInfo()) {
          this.hasLoggedTokenInfo.set(true);
          this.logKeycloakTokensToConsole();
        }

        this.hasRedirected.set(true);

        const returnUrl = this.getReturnUrl();

        // Pequeno delay para garantir que tudo foi processado
        setTimeout(() => {
          this.navigateToReturnUrl(returnUrl);
        }, 100);
      }
    });

    // Watchdog: se nada resolver (sucesso ou erro) dentro do prazo, sai do spinner.
    setTimeout(() => {
      if (!this.hasRedirected()) {
        console.warn('[AUTH-CALLBACK] Timeout aguardando processamento do callback');
        this.timedOut.set(true);
      }
    }, WATCHDOG_TIMEOUT_MS);
  }

  /**
   * Navega para a URL de retorno. Se a navegação do router falhar (ex.: chunk lazy
   * quebrado por cache de service worker desatualizado após deploy), força uma
   * navegação de documento completa — que busca os assets direto da rede.
   */
  private navigateToReturnUrl(returnUrl: string): void {
    this.router.navigateByUrl(returnUrl).then(
      (navegou) => {
        if (!navegou) {
          this.hardNavigate(returnUrl);
        }
      },
      () => {
        this.hardNavigate(returnUrl);
      }
    );
  }

  protected retry(): void {
    if (this.timedOut() || this.authService.authError()) {
      this.hardNavigate('/');
      return;
    }

    this.authService.login();
  }

  /** Navegação de documento completo — escapa de router/SW em estado quebrado (extraído para testabilidade). */
  private hardNavigate(url: string): void {
    window.location.assign(url);
  }

  private getReturnUrl(): string {
    // Tenta recuperar URL de retorno do sessionStorage
    const returnUrl = sessionStorage.getItem('auth_return_url');
    if (returnUrl) {
      sessionStorage.removeItem('auth_return_url');
      return returnUrl;
    }
    return '/inicio';
  }

  private logKeycloakTokensToConsole(): void {
    if (!environment.features.enableDebugTools) {
      return;
    }

    forkJoin({
      accessToken: this.oidcSecurityService.getAccessToken().pipe(take(1)),
      accessTokenPayload: this.oidcSecurityService.getPayloadFromAccessToken().pipe(take(1)),
      userData: this.oidcSecurityService.getUserData().pipe(take(1)),
    }).subscribe({
      next: ({ accessToken, accessTokenPayload, userData }) => {
        const exp = typeof accessTokenPayload?.exp === 'number' ? accessTokenPayload.exp : undefined;
        const iat = typeof accessTokenPayload?.iat === 'number' ? accessTokenPayload.iat : undefined;

        const expDate = exp ? new Date(exp * 1000) : undefined;
        const iatDate = iat ? new Date(iat * 1000) : undefined;

        console.groupCollapsed('[AUTH] Tokens recebidos do Keycloak (DEBUG)');

        console.log('[AUTH] Access token (JWT):', accessToken);
        console.log('[AUTH] Access token payload (decodificado):', accessTokenPayload);
        console.log('[AUTH] UserData (claims do ID Token):', userData);

        if (expDate || iatDate) {
          console.table([
            {
              iat,
              iatISO: iatDate?.toISOString(),
              exp,
              expISO: expDate?.toISOString(),
            },
          ]);
        }

        console.groupEnd();
      },
      error: (err) => {
        console.warn('[AUTH] Falha ao capturar tokens para debug:', err);
      },
    });
  }
}
